import React, { useState } from 'react';
import { Eye } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';
import { createLogger } from '../../utils/logger';
import { roleLabel } from '../../utils/roleLabel';
import './DemoProxyBanner.scss';

const logger = createLogger('DemoProxyBanner');


/**
 * Always-visible reminder that the app is being viewed as another role, with a
 * one-click way out.
 *
 * The proxy is server-side and outlives a page reload, so without this a super admin
 * could easily forget they are in a demo view and mistake missing menu items for a
 * bug — or, worse, act on org-scoped data thinking it was platform-wide.
 */
const DemoProxyBanner: React.FC = () => {
  const { t } = useTranslation();
  const { isProxyActive, userRole, userData, stopDemoProxy } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!isProxyActive || !userRole) return null;

  const orgName = userData?.organization?.name;

  const handleExit = async () => {
    setBusy(true);
    try {
      await stopDemoProxy();
    } catch (e) {
      logger.error('Failed to end the demo proxy', e);
      setBusy(false);
    }
  };

  return (
    <div className="demo-proxy-banner" role="status" data-testid="demo-proxy-banner">
      <Eye size={15} className="demo-proxy-banner__icon" />
      <span className="demo-proxy-banner__text">
        <Trans
          i18nKey={orgName ? 'misc.demoProxy.bannerInOrg' : 'misc.demoProxy.banner'}
          values={{ role: roleLabel(userRole, t), org: orgName }}
          components={{ bold: <strong /> }}
          // The org name is user-typed: escape it before Trans parses the string for tags
          // (an org called "R&D <Lab>" would lose "<Lab>"), then show it unescaped.
          tOptions={{ interpolation: { escapeValue: true } }}
          shouldUnescape
        />
      </span>
      <button
        type="button"
        className="demo-proxy-banner__exit"
        onClick={handleExit}
        disabled={busy}
      >
        {busy ? t('misc.demoProxy.exiting') : t('misc.demoProxy.exitDemoView')}
      </button>
    </div>
  );
};

export default DemoProxyBanner;
